import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Textarea } from '@components';
import { CreateAgentData } from '../../types';
import { useApiEndpoints } from '../../hooks/useApiEndpoints';
import { calendarIntegrationService } from '../../../Calendars/services/calendarIntegration';
import { Calendar } from '../../../Calendars/types';
import { ApiEndpointsSection } from './ApiEndpointsSection';
import { VariableExtractionBuilder } from '../VariableExtractionBuilder';

interface CallSettingsTabProps {
  formData: CreateAgentData;
  onInputChange: (
    field: keyof CreateAgentData,
    value: string | number | boolean | string[] | unknown[]
  ) => void;
  errors: Record<string, string>;
}

const CallSettingsTab: React.FC<CallSettingsTabProps> = ({ formData, onInputChange, errors }) => {
  const { t } = useTranslation();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [calendarsLoading, setCalendarsLoading] = useState(true);

  // Extract variable names from variable_extraction schema
  const extractedVariables = React.useMemo(() => {
    if (!formData.variable_extraction) return [];

    try {
      const parsed = JSON.parse(formData.variable_extraction);
      if (parsed.schema?.properties) {
        return Object.keys(parsed.schema.properties);
      }
    } catch (e) {
      // Invalid JSON, return empty array
    }
    return [];
  }, [formData.variable_extraction]);

  const {
    apiEndpoints,
    updateApiEndpoint,
    addApiEndpoint,
    removeApiEndpoint,
    addHeader,
    removeHeader,
    updateHeader,
  } = useApiEndpoints({
    formDataCalls: formData.live_api_calls || [],
    callType: 'live',
    onSync: calls => onInputChange('live_api_calls', calls),
  });

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setCalendarsLoading(true);
        const calendarsList = await calendarIntegrationService.list();
        setCalendars(calendarsList);
      } catch (error) {
        setCalendars([]);
      } finally {
        setCalendarsLoading(false);
      }
    };
    fetchCalendars();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.duringTheCall')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.duringTheCallDescription')}
        </p>
      </div>

      <div className="space-y-8">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              data_object
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.dataExtraction')}
            </h4>
          </div>

          <VariableExtractionBuilder
            value={formData.variable_extraction || ''}
            onChange={value => onInputChange('variable_extraction', value)}
            error={errors.variable_extraction}
          />
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              search
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.webSearch')}
            </h4>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="web-search"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.webSearch')}
              </label>
              <Select
                id="web-search"
                value={formData.web_search_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const webSearchValue = Array.isArray(value) ? value[0] : value;
                  onInputChange('web_search_enabled', webSearchValue === 'enabled');
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
              />
            </div>

            {formData.web_search_enabled && (
              <>
                <div>
                  <label
                    htmlFor="search-scope"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.searchScope')}
                  </label>
                  <div className="space-y-2">
                    <label htmlFor="search-scope-whitelist" className="flex items-center">
                      <input
                        id="search-scope-whitelist"
                        type="radio"
                        name="webSearchScope"
                        value="whitelist"
                        checked={!formData.all_internet}
                        onChange={() => onInputChange('all_internet', false)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('agents.whitelistWebsites')}
                      </span>
                    </label>
                    <label htmlFor="search-scope-all" className="flex items-center">
                      <input
                        id="search-scope-all"
                        type="radio"
                        name="webSearchScope"
                        value="all"
                        checked={formData.all_internet}
                        onChange={() => onInputChange('all_internet', true)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('agents.allInternet')}
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="whitelisted-websites"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('agents.whitelistedWebsites')}
                  </label>
                  <Textarea
                    id="whitelisted-websites"
                    className="w-full"
                    rows={3}
                    value={formData.whitelist_websites || ''}
                    onChange={e => onInputChange('whitelist_websites', e.target.value)}
                    placeholder="Enter website URLs (one per line):&#10;https://example.com&#10;https://docs.example.com"
                    state={errors.whitelist_websites ? 'error' : 'default'}
                    errorText={errors.whitelist_websites}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
              integration_instructions
            </span>
            <h4 className="text-md font-semibold text-gray-900 dark:text-white">
              {t('agents.integrations')}
            </h4>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="calendar"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.calendarIntegration')}
              </label>
              <Select
                id="calendar"
                value={formData.calendar_integration || ''}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  onInputChange('calendar_integration', selectedValue || '');
                }}
                placeholder={t('agents.selectOption')}
                options={[
                  { value: '', label: t('agents.noCalendar') || 'Kein Kalender' },
                  ...calendars.map(calendar => ({
                    value: calendar.id,
                    label: `${calendar.name || calendar.provider_display} (${calendar.email})`,
                  })),
                ]}
                className="w-full"
                disabled={calendarsLoading}
                error={errors.calendar_integration ? true : undefined}
                errorText={errors.calendar_integration}
              />
            </div>

            <div>
              <label
                htmlFor="live-api-calls"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.liveApiCalls')}
              </label>
              <Select
                id="live-api-calls"
                value={formData.live_api_calls_enabled ? 'enabled' : 'disabled'}
                onChange={value => {
                  const selectedValue = Array.isArray(value) ? value[0] : value;
                  const booleanValue = selectedValue === 'enabled';
                  onInputChange('live_api_calls_enabled', booleanValue);
                }}
                options={[
                  { value: 'disabled', label: t('agents.disabled') },
                  { value: 'enabled', label: t('agents.enabled') },
                ]}
                className="w-full"
                error={errors.live_api_calls_enabled ? true : undefined}
                errorText={errors.live_api_calls_enabled}
              />
            </div>
          </div>
        </div>

        {formData.live_api_calls_enabled && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                api
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.apiEndpointsConfiguration')}
              </h4>
            </div>

            <ApiEndpointsSection
              apiEndpoints={apiEndpoints}
              updateApiEndpoint={updateApiEndpoint}
              addApiEndpoint={addApiEndpoint}
              removeApiEndpoint={removeApiEndpoint}
              addHeader={addHeader}
              removeHeader={removeHeader}
              updateHeader={updateHeader}
              extractedVariables={extractedVariables}
              urlPlaceholder="https://api.example.com/endpoint"
              requestBodyPlaceholder={`{
  "call_id": "{{ call_id }}",
  "customer_id": "{{ customer_id }}",
  "summary": "{{ conversation_summary }}",
  "duration": "{{ call_duration }}",
  "status": "completed"
}`}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { CallSettingsTab };
